// Los bloques de una página del sitio, en orden. Componentes de
// presentación: los datos llegan resueltos del servidor (`getPage`) y las
// acciones (elegir, enviar) se reciben por props. Un bloque que se carga
// aparte llega como marcador (`deferred`) y lo pinta `renderDeferred`. Una
// sección plegable pinta sus bloques adentro con las mismas reglas.

import { Fragment, type ReactNode } from "react";
import type { DeferredBlock, PageBlock, ResolvedBlock } from "../values";
import { BlockSkeleton } from "./block-skeleton";
import { ChartBlock } from "./chart-block";
import { FormBlock } from "./form-block";
import { GridBlock } from "./grid-block";
import { ListBlock } from "./list-block";
import { PdfBlock, type SitePdfFile } from "./pdf-block";
import { PickerBlock } from "./picker-block";
import { SectionBlock } from "./section-block";
import { StatBlock } from "./stat-block";
import { HeadingBlock, TextBlock } from "./text-blocks";

/** Acciones de los bloques (elegir, enviar, descargar). */
export interface SiteBlockActions {
  onPick: (source: string, id: string) => void;
  onSubmit: (block: string, values: Record<string, unknown>) => Promise<unknown>;
  /** PDF de un bloque `pdf` (`choice`: lo elegido; vacío = todo). */
  onDownload: (block: string, choice: string | undefined) => Promise<SitePdfFile>;
}

export interface SiteBlocksProps extends SiteBlockActions {
  blocks: PageBlock[];
  /**
   * Un bloque que se carga aparte (el sitio público lo pide con `getBlock`).
   * Sin él, su esqueleto.
   */
  renderDeferred?: (block: DeferredBlock) => ReactNode;
  /**
   * Solo el editor de sitios: envuelve cada bloque (marco para elegirlo en la
   * vista previa). Sin él, el sitio público se pinta igual que siempre.
   */
  wrap?: (block: ResolvedBlock, node: ReactNode) => ReactNode;
}

function renderBlock(block: Exclude<ResolvedBlock, { type: "stat" }>, props: SiteBlockActions): ReactNode {
  switch (block.type) {
    case "heading":
      return <HeadingBlock block={block} />;
    case "text":
      return <TextBlock block={block} />;
    case "list":
      return <ListBlock block={block} />;
    case "grid":
      return <GridBlock block={block} />;
    case "chart":
      return <ChartBlock block={block} />;
    case "picker":
      return <PickerBlock block={block} onPick={props.onPick} />;
    case "form":
      return <FormBlock block={block} onSubmit={(values) => props.onSubmit(block.key, values)} />;
    case "pdf":
      return <PdfBlock block={block} onDownload={(choice) => props.onDownload(block.key, choice)} />;
    case "section":
      // Las secciones las pinta `SiteBlocks` (necesitan pintar sus bloques).
      return null;
  }
}

type StatLike = Extract<PageBlock, { type: "stat" }>;

/** Un bloque ya resuelto, sin marco (lo usa también el bloque que se carga aparte). */
export function SiteBlock({ block, ...actions }: SiteBlockActions & { block: ResolvedBlock }) {
  return block.type === "stat" ? <StatBlock block={block} /> : renderBlock(block, actions);
}

export function SiteBlocks(props: SiteBlocksProps) {
  const one = (block: PageBlock): ReactNode => {
    if ("deferred" in block) {
      return props.renderDeferred ? props.renderDeferred(block) : <BlockSkeleton block={block} />;
    }
    if (block.type === "section") {
      const node = (
        <SectionBlock title={block.title} count={block.showCount ? block.blocks.length : undefined} defaultOpen={block.open}>
          <SiteBlocks {...props} blocks={block.blocks} />
        </SectionBlock>
      );
      return props.wrap ? props.wrap(block, node) : node;
    }
    const node = <SiteBlock block={block} onPick={props.onPick} onSubmit={props.onSubmit} onDownload={props.onDownload} />;
    return props.wrap ? props.wrap(block, node) : node;
  };
  // Estadísticas seguidas van juntas, en una grilla (también las que se cargan aparte).
  const groups: Array<PageBlock | StatLike[]> = [];
  for (const block of props.blocks) {
    const last = groups[groups.length - 1];
    if (block.type === "stat" && Array.isArray(last)) last.push(block as StatLike);
    else groups.push(block.type === "stat" ? [block as StatLike] : block);
  }
  return (
    <div className="space-y-6">
      {groups.map((group) =>
        Array.isArray(group) ? (
          <div key={group.map((s) => s.key).join("|")} className="grid grid-cols-2 gap-3 @min-[40rem]:grid-cols-3">
            {group.map((s) => (
              <Fragment key={s.key}>{one(s)}</Fragment>
            ))}
          </div>
        ) : (
          <div key={group.key}>{one(group)}</div>
        ),
      )}
    </div>
  );
}
